import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type {
    LayoutModeType,
    LayoutOffcanvasStatesType,
    LayoutOrientationType,
    LayoutState,
    LayoutType,
    MenuType,
    OffcanvasControlType,
    ThemeType,
    TopBarThemeType,
} from '@/types/context';

import useLocalStorage from '@/hooks/useLocalStorage';
import useQueryParams from '@/hooks/useQueryParams';
import { ChildrenType } from '@/types/component-props';
import { toggleDocumentAttribute } from '@/utils/layout';

const ThemeContext = createContext<LayoutType | undefined>(undefined);

const useLayoutContext = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useLayoutContext can only be used within LayoutProvider');
    }
    return context;
};

const LayoutProvider = ({ children }: ChildrenType) => {
    const queryParams = useQueryParams();

    const override = !!(
        queryParams.layout_theme ||
        queryParams.topbar_theme ||
        queryParams.menu_theme ||
        queryParams.menu_size ||
        queryParams.layout_mode
    );

    if (typeof window !== 'undefined' && !window.localStorage.getItem('__GD_THEME_DEFAULTS_V1__')) {
        try {
            const raw = window.localStorage.getItem('__OSEN_CONFIG__');
            if (raw) {
                const saved = JSON.parse(raw);
                saved.theme = 'light';
                saved.topbarTheme = 'light';
                saved.menu = { ...(saved.menu ?? {}), theme: 'light', size: saved?.menu?.size ?? 'default' };
                saved.layoutMode = saved.layoutMode ?? 'fluid';
                window.localStorage.setItem('__OSEN_CONFIG__', JSON.stringify(saved));
            }
            window.localStorage.setItem('__GD_THEME_DEFAULTS_V1__', '1');
        } catch {
            window.localStorage.setItem('__GD_THEME_DEFAULTS_V1__', '1');
        }
    }

    const INIT_STATE: LayoutState = {
        theme: queryParams['layout_theme'] ? (queryParams['layout_theme'] as ThemeType) : 'light',
        layoutOrientation: queryParams['layout_type'] ? (queryParams['layout_type'] as LayoutOrientationType) : 'vertical',
        topbarTheme: queryParams['topbar_theme'] ? (queryParams['topbar_theme'] as TopBarThemeType) : 'light',
        menu: {
            theme: queryParams['menu_theme'] ? (queryParams['menu_theme'] as MenuType['theme']) : 'light',
            size: queryParams['menu_size'] ? (queryParams['menu_size'] as MenuType['size']) : 'default',
        },
        layoutMode: queryParams['layout_mode'] ? (queryParams['layout_mode'] as LayoutModeType) : 'fluid',
    };

    const [settings, setSettings] = useLocalStorage<LayoutState>('__OSEN_CONFIG__', INIT_STATE, override);
    const [offcanvasStates, setOffcanvasStates] = useState<LayoutOffcanvasStatesType>({
        showThemeCustomizer: false,
        showHorizontalMenu: false,
        // showActivityStream: false,
        showBackdrop: false,
    });

    // update settings
    const updateSettings = (_newSettings: Partial<LayoutState>) => setSettings({ ...settings, ..._newSettings });

    // update theme mode
    const changeTheme = (newTheme: ThemeType) => {
        updateSettings({ theme: newTheme });
    };

    // change topbar theme
    const changeTopbarTheme = (newTheme: TopBarThemeType) => {
        updateSettings({ topbarTheme: newTheme });
    };

    const changeLayoutMode = (newMode: LayoutModeType) => {
        updateSettings({ layoutMode: newMode });
    };

    const changeLayoutOrientation = (newOrientation: LayoutOrientationType) => {
        updateSettings({ layoutOrientation: newOrientation });
    };

    // change menu theme
    const changeMenuTheme = (newTheme: MenuType['theme']) => {
        updateSettings({ menu: { ...settings.menu, theme: newTheme } });
    };

    // change menu theme
    const changeMenuSize = (newSize: MenuType['size']) => {
        updateSettings({ menu: { ...settings.menu, size: newSize } });
    };

    // toggle theme customizer offcanvas
    const toggleThemeCustomizer: OffcanvasControlType['toggle'] = () => {
        setOffcanvasStates({ ...offcanvasStates, showThemeCustomizer: !offcanvasStates.showThemeCustomizer });
    };

    const toggleHorizontalMenu: OffcanvasControlType['toggle'] = () => {
        setOffcanvasStates({ ...offcanvasStates, showHorizontalMenu: !offcanvasStates.showHorizontalMenu });
    };

    const themeCustomizer: LayoutType['themeCustomizer'] = {
        open: offcanvasStates.showThemeCustomizer,
        toggle: toggleThemeCustomizer,
    };

    const horizontalMenu: LayoutType['horizontalMenu'] = {
        open: offcanvasStates.showHorizontalMenu,
        toggle: toggleHorizontalMenu,
    };

    // toggle backdrop
    const toggleBackdrop = useCallback(() => {
        const htmlTag = document.getElementsByTagName('html')[0];
        const isClosing = offcanvasStates.showBackdrop;

        if (isClosing) htmlTag.classList.remove('sidebar-enable');
        else htmlTag.classList.add('sidebar-enable');

        if (isClosing && window.innerWidth <= 768 && settings.menu.size === 'full') {
            setSettings({ ...settings, menu: { ...settings.menu, size: 'condensed' } });
        }

        setOffcanvasStates({ ...offcanvasStates, showBackdrop: !offcanvasStates.showBackdrop });
    }, [offcanvasStates, settings, setSettings]);

    useEffect(() => {
        toggleDocumentAttribute('data-bs-theme', settings.theme);
        toggleDocumentAttribute('data-topbar-color', settings.topbarTheme);
        toggleDocumentAttribute('data-menu-color', settings.menu.theme);
        toggleDocumentAttribute('data-sidenav-size', settings.menu.size);
        toggleDocumentAttribute('data-layout-mode', settings.layoutMode);
        toggleDocumentAttribute('data-layout', settings.layoutOrientation === 'vertical' ? '' : 'topnav');
        return () => {
            toggleDocumentAttribute('data-bs-theme', settings.theme, true);
            toggleDocumentAttribute('data-topbar-color', settings.topbarTheme, true);
            toggleDocumentAttribute('data-sidenav-size', settings.menu.theme, true);
            toggleDocumentAttribute('data-layout-mode', settings.layoutMode, true);
            toggleDocumentAttribute('data-layout', settings.layoutOrientation === 'vertical' ? '' : 'topnav', true);
        };
    }, [settings]);

    const resetSettings = () => updateSettings(INIT_STATE);

    return (
        <ThemeContext.Provider
            value={useMemo(
                () => ({
                    ...settings,
                    horizontalMenu,
                    changeTheme,
                    changeLayoutMode,
                    changeTopbarTheme,
                    changeLayoutOrientation,
                    changeMenu: {
                        theme: changeMenuTheme,
                        size: changeMenuSize,
                    },
                    themeCustomizer,
                    toggleBackdrop,
                    resetSettings,
                }),
                [settings, offcanvasStates],
            )}
        >
            {children}
            {offcanvasStates.showBackdrop && <div className="offcanvas-backdrop fade show" onClick={toggleBackdrop} />}
        </ThemeContext.Provider>
    );
};

export { LayoutProvider, useLayoutContext };
